import React, { useState, useEffect } from "react"
import { Button, Form, Dropdown, DropdownButton, ButtonGroup } from "react-bootstrap"
import { FaPlus, FaEllipsisV, FaTag, FaSearch } from "react-icons/fa"
import CreateAddOns from "./CreateAddOns"
import { MDBDataTable } from "mdbreact"
import axios from "axios"
import { localserver } from "../../../api/common"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"
import { useUser } from "../../AdminUsers/UserContext"
import { RiCoupon5Line } from "react-icons/ri"

const EventAddOns = ({ eventId }) => {
  const { checkPermission } = useUser()
  const [searchTerm, setSearchTerm] = useState("")
  const [activeTab, setActiveTab] = useState("All")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedAddOns, setSelectedAddOns] = useState([])
  const [addOns, setAddOns] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedTicketId, setSelectedTicketId] = useState("0")
  const [selectedTicketName, setSelectedTicketName] = useState("All Tickets")
  const [ticketList, setTicketList] = useState([])
  const [addonToEdit, setAddonToEdit] = useState(null)

  // Add CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .dataTables_filter {
        display: none !important;
      }
    `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  const fetchAddOns = async (filters = {}) => {
    setIsLoading(true)
    setError(null)
    const storedEventId = localStorage.getItem("eventId") || eventId
    if (!storedEventId) {
      setError("No event ID found. Please select an event.")
      showToast("No event ID found. Please select an event.", "error")
      setIsLoading(false)
      return
    }

    try {
      const response = await axios.post(
        `${localserver}/get_addons`,
        {
          event_id: localStorage.getItem("eventId") || eventId,
          addon_status: "all",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success && Array.isArray(response.data.addons)) {
        const mappedAddOns = response.data.addons.map((addon) => ({
          id: addon.id,
          name: addon.add_on_name || "Unnamed Add-on",
          icon: "tag",
          iconBgColor: addon.addons_type === "variant" ? "#184778" : "#F97316",
          variants: Array.isArray(addon.variant_options) ? addon.variant_options.length : 0,
          linkedTo: Array.isArray(addon.linked_tickets) ? `${addon.linked_tickets.length} tickets` : "-",
          tickets: Array.isArray(addon.linked_tickets) ? addon.linked_tickets.length : 0,
          qtyAvailable: Array.isArray(addon.variant_options)
            ? addon.variant_options.reduce(
                (sum, v) => sum + (Number(v["Quantity available"] || v.quantity_available) || 0),
                0,
              )
            : addon.quantity === null || Number(addon.quantity) === 0
              ? "∞"
              : Number(addon.quantity),
          qtySold: Number(addon.sold_quantity) || 0,
          price:
            addon.price_type === "free"
              ? "0"
              : Array.isArray(addon.variant_options) && addon.variant_options.length > 0
                ? Number(addon.variant_options[0]["Variant price"] || addon.variant_options[0].variant_price) || 0
                : Number(addon.price) || 0,
          currency: addon.currency || "USD",
          status: addon.addon_status === "onsale" ? true : false,
          rawData: addon,
        }))
        setAddOns(mappedAddOns)
        setSelectedAddOns(selectedAddOns.filter((id) => mappedAddOns.some((addon) => addon.id === id)))
      } else {
        setError("No add-ons found for this event.")
        showToast("No add-ons found for this event.", "error")
      }
    } catch (error) {
      console.error("Error fetching add-ons:", error)
      setError("Failed to fetch add-ons. Please try again.")
      showToast("Failed to fetch add-ons. Please try again.", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const fetchTickets = async () => {
    try {
      const response = await axios.post(
        `${localserver}/get_ticket_by_event_id`,
        {
          event_id: localStorage.getItem("eventId") || eventId,
          ticket_status: "all",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success && Array.isArray(response.data.tickets)) {
        setTicketList(response.data.tickets)
      } else {
        showToast("No tickets found for this event.", "error")
      }
    } catch (error) {
      console.error("Error fetching tickets:", error)
      showToast("Failed to fetch tickets. Please try again.", "error")
    }
  }

  useEffect(() => {
    fetchAddOns()
    fetchTickets()
  }, [eventId])

  const handleSelectAll = (event) => {
    const isChecked = event?.target?.checked ?? false
    if (isChecked) {
      setSelectedAddOns(addOns.map((addon) => addon.id))
    } else {
      setSelectedAddOns([])
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  const handleSearch = (e) => {
    const query = e.target.value
    setSearchTerm(query)
  }

  const handleTicketChange = (eventKey) => {
    setSelectedTicketId(eventKey)
    if (eventKey === "0") {
      setSelectedTicketName("All Tickets")
    } else {
      const selected = ticketList.find((ticket) => ticket.id.toString() === eventKey.toString())
      setSelectedTicketName(selected?.ticket_name || "Unknown Ticket")
    }
    fetchAddOns({ ticket_id: eventKey, search: searchTerm })
  }

  const handleCreateAddOn = (newAddOn) => {
    fetchAddOns()
    setShowCreateModal(false)
    setAddonToEdit(null)
  }

  const handleEditAddOn = async (addon) => {
    const hasPermission = await checkPermission("Addons", "update")
    if (!hasPermission) return
    setAddonToEdit(addon.rawData)
    setShowCreateModal(true)
  }

  const handleSelectAddOn = (id) => {
    if (selectedAddOns.includes(id)) {
      setSelectedAddOns(selectedAddOns.filter((addonId) => addonId !== id))
    } else {
      setSelectedAddOns([...selectedAddOns, id])
    }
  }

  const handleToggleStatus = async (id, value) => {
    if (value === "deleted") {
      const hasPermission = await checkPermission("Coupens", "delete")
      if (!hasPermission) return
    } else if (value === "archived") {
      const hasPermission = await checkPermission("Coupens", "update")
      if (!hasPermission) return
    }
    let addOnStatus = ""
    if (value === "deleted") {
      addOnStatus = "deleted"
    } else if (value === "archived") {
      addOnStatus = "archived"
    } else {
      addOnStatus = value ? "onsale" : "paused"
    }

    try {
      const response = await axios.post(
        `${localserver}/update_addonstatus`,
        {
          addon_id: id,
          event_id: localStorage.getItem("eventId") || eventId,
          addon_status: addOnStatus,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success) {
        setAddOns(addOns.map((addon) => (addon.id === id ? { ...addon, status: value } : addon)))
        showToast(response.data.message, "success")
        fetchAddOns()
      } else {
        showToast(response.data.message || "Failed to update add-on status.", "error")
      }
    } catch (error) {
      console.error("Error updating add-on status:", error)
      showToast("Failed to update add-on status. Please try again.", "error")
    }
  }

  const filteredAddOns = addOns.filter((addon) => {
    // Filter by tab
    let tabMatch = true
    if (activeTab === "All") tabMatch = true
    else if (activeTab === "On Sale") tabMatch = addon.status === true
    else if (activeTab === "Paused") tabMatch = addon.status === false
    else if (activeTab === "Drafts") tabMatch = addon.rawData.addon_status === "draft"
    else if (activeTab === "Archived") tabMatch = addon.rawData.addon_status === "archived"
    else if (activeTab === "Deleted") tabMatch = addon.rawData.addon_status === "deleted"

    // Filter by search term
    const searchMatch = addon.name.toLowerCase().includes(searchTerm.toLowerCase())

    return tabMatch && searchMatch
  })

  const getStatusBadge = (addon) => {
    return (
      <label className="switch">
        <input
          type="checkbox"
          className="lv-btn small-switch"
          checked={addon.status}
          onChange={(e) => handleToggleStatus(addon.id, e.target.checked)}
        />
        <span className="slider round"></span>
      </label>
    )
  }

  const handleCreateAddOnOpen = async () => {
    const hasPermission = await checkPermission("Addons", "create")
    if (!hasPermission) return
    setShowCreateModal(true)
  }

  // Check Admin User Permission
  const [checkUserLoading, setCheckUserLoading] = useState(false)
  const fetchPermission = async () => {
    setCheckUserLoading(true)
    try {
      const hasPermission = await checkPermission("Addons", "view")
      if (!hasPermission) return
    } catch (error) {
      console.error("Error Check Permission:", error)
    } finally {
      setCheckUserLoading(false)
    }
  }

  useEffect(() => {
    fetchPermission()
  }, [])

  // JSX Methods
  const renderHeader = () => (
    <div style={{ marginBottom: "22px" }}>
      <div className="brood_comes_div">
        <div className="row align-items-center">
          <div className="col-md-6 brood_comes_heading">
            <h2>
              <div className="brood_comes_part">
                <RiCoupon5Line size={18} />
              </div>
              Add-ons
            </h2>
          </div>
          <div className="col-md-6 text-end">
            <button className="btn btn-outline-primary " onClick={() => handleCreateAddOnOpen()}>
              <FaPlus />
              Create Add-on
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderStatsCards = () => {
    return <></>
  }

  const renderFiltersAndTabs = () => {
    const tabs = [
      { key: "All", label: `All (${addOns.length})` },
      {
        key: "On Sale",
        label: `On Sale (${addOns.filter((a) => a.status === true).length})`,
      },
      {
        key: "Paused",
        label: `Paused (${addOns.filter((a) => a.status === false).length})`,
      },
      {
        key: "Drafts",
        label: `Drafts (${addOns.filter((a) => a.rawData.addon_status === "draft").length})`,
      },
      {
        key: "Archived",
        label: `Archived (${addOns.filter((a) => a.rawData.addon_status === "archived").length})`,
      },
      {
        key: "Deleted",
        label: `Deleted (${addOns.filter((a) => a.rawData.addon_status === "deleted").length})`,
      },
    ]

    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "22px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
        }}
      >
        {/* Tabs and Search */}
        <div className="addon_tab_total">
          <div className="addon_tab_inner">
            {tabs.map((tab) => (
              <div
                key={tab.key}
                style={{
                  padding: "12px 20px",
                  border: "none",
                  borderRadius: "8px",
                  background: activeTab === tab.key ? "rgb(24, 71, 120)" : "transparent",
                  color: activeTab === tab.key ? "#ffffff" : "rgb(102, 102, 102)",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                  boxShadow: activeTab === tab.key ? "0 2px 8px rgba(24, 71, 120, 0.2)" : "none",
                  transform: activeTab === tab.key ? "translateY(-1px)" : "translateY(0)",
                }}
                onClick={() => handleTabChange(tab.key)}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.target.style.background = "#e9e9e9ff"
                    e.target.style.color = "#184778"
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.target.style.background = "transparent"
                    e.target.style.color = "rgb(102, 102, 102)"
                  }
                }}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {/* Search Field */}
          <div>
            <div style={{ position: "relative", maxWidth: "400px" }}>
              <FaSearch
                style={{
                  position: "absolute",
                  left: "16px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6b7280",
                  fontSize: "16px",
                }}
              />
              <input
                type="text"
                placeholder="Search add-ons..."
                value={searchTerm}
                onChange={handleSearch}
                style={{
                  width: "100%",
                  padding: "12px 16px 12px 44px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#184778"
                  e.target.style.boxShadow = "0 0 0 3px rgba(24, 71, 120, 0.1)"
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb"
                  e.target.style.boxShadow = "none"
                }}
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDataTable = () => {
    const tableData = {
      columns: [
        { label: "Add-on Name", field: "name", sort: "asc" },
        { label: "Linked to", field: "linkedTo", sort: "asc" },
        { label: "Qty Available", field: "qtyAvailable", sort: "asc" },
        { label: "Qty Sold", field: "qtySold", sort: "asc" },
        { label: "Price", field: "price", sort: "asc" },
        { label: "Status", field: "status", sort: "asc" },
        { label: "", field: "actions", sort: "disabled", width: 40 },
      ],
      rows: filteredAddOns.map((addon) => ({
        checkbox: (
          <Form.Check
            type="checkbox"
            label=""
            checked={selectedAddOns.includes(addon.id)}
            onChange={() => handleSelectAddOn(addon.id)}
            disabled={isLoading}
          />
        ),
        name: (
          <div className="d-flex align-items-center gap-7">
            <div
              className="me-3 d-flex justify-content-center align-items-center border_radius_7"
              style={{
                width: 32,
                height: 32,
                backgroundColor: addon.iconBgColor,
                color: "#fff",
              }}
            >
              <FaTag />
            </div>
            <div>
              <div>{addon.name}</div>
              <div className="text-muted small">
                {addon.variants} {addon.variants === 1 ? "variant" : "variants"}
                <Button
                  onClick={() => handleEditAddOn(addon)}
                  variant="link"
                  size="sm"
                  className="ml-2 custom_button_out btn btn-outline-default"
                >
                  Show
                </Button>
              </div>
            </div>
          </div>
        ),
        linkedTo: addon.linkedTo,
        qtyAvailable: addon.qtyAvailable,
        qtySold: addon.qtySold,
        price:
          addon.price === "0" ? (
            <span className="fee_table_free">Free</span>
          ) : (
            <span className="fee_table_price">
              {addon.currency} {addon.price}
            </span>
          ),
        status: getStatusBadge(addon),
        actions: (
          <Dropdown as={ButtonGroup}>
            <DropdownButton variant="link" id={`dropdown-actions-${addon.id}`} title={<FaEllipsisV />} size="sm">
              <Dropdown.Item onClick={() => handleEditAddOn(addon)}>Edit</Dropdown.Item>
              <Dropdown.Item onClick={() => handleToggleStatus(addon.id, "deleted")}>Delete</Dropdown.Item>
              <Dropdown.Item onClick={() => handleToggleStatus(addon.id, "archived")}>Archived</Dropdown.Item>
            </DropdownButton>
          </Dropdown>
        ),
      })),
    }

    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "8px",
              padding: "16px",
              margin: "20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <i
              className="fa fa-exclamation-triangle"
              style={{
                color: "#dc2626",
                fontSize: "20px",
              }}
            ></i>
            <p
              style={{
                color: "#dc2626",
                margin: 0,
              }}
            >
              {error}
            </p>
          </div>
        )}

        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "40px",
              color: "#6b7280",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "3px solid #e5e7eb",
                borderTop: "3px solid #184778",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginRight: "12px",
              }}
            ></div>
            <span>Loading add-ons...</span>
          </div>
        ) : (
          <>
            <div style={{ padding: "32px" }}>
              <MDBDataTable
                noBottomColumns
                searching={false}
                className="table table-hover js-basic-example dataTable table-custom spacing5"
                entriesOptions={[5, 10, 20, 25]}
                entries={10}
                paging
                pagesAmount={5}
                data={tableData}
                sortRows={["name"]}
                preserveScroll={true}
                preserveState={true}
              />
            </div>
            <div
              style={{
                padding: "16px 20px",
                borderTop: "1px solid #e5e7eb",
                background: "#f9fafb",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Showing 1 to {filteredAddOns.length} records of {addOns.length}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <>
      {checkUserLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "500px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div
          className="container-fluid"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f8fafc",
            padding: "24px 15px",
          }}
        >
          <ToastMessage />

          {renderHeader()}
          {renderStatsCards()}
          {renderFiltersAndTabs()}
          {renderDataTable()}

          <CreateAddOns
            show={showCreateModal}
            onHide={() => {
              setShowCreateModal(false)
              setAddonToEdit(null)
            }}
            onSubmit={handleCreateAddOn}
            eventId={eventId}
            addonToEdit={addonToEdit}
          />
        </div>
      )}
    </>
  )
}

export default EventAddOns
